import { Component, OnInit, Input, OnChanges, SimpleChanges, EventEmitter, Output, OnDestroy } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ApiService, DialogService, CoreUiPartialState, hasUnsavedChanges, getBrandDefaults } from '@pos-app/core-ui';
import { Router } from '@angular/router';
import { CUSTOMER_TYPE, LookupLists, CustomerDetails, State, BrandDefaults } from '@pos-app/data';
import { Subject, BehaviorSubject, Observable } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, filter, concatMap } from 'rxjs/operators';
import { Customer } from '@pos-app/data';
import { Store } from '@ngrx/store';
import { NgIf, NgFor, NgClass } from '@angular/common';
import { CoreUiModule } from '../../../../../../../../libs/core-ui/src/lib/core-ui.module';
import { NgSelectComponent, NgOptionComponent } from '@ng-select/ng-select';

@Component({
    selector: 'app-customers-details-form',
    templateUrl: './customers-details-form.component.html',
    styleUrls: ['./customers-details-form.component.scss'],
    imports: [NgIf, FormsModule, ReactiveFormsModule, CoreUiModule, NgSelectComponent, NgFor, NgOptionComponent, NgClass]
})
export class CustomersDetailsFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() selectedCustomer: CustomerDetails;
  @Input() lookupLists: LookupLists;
  @Input() brandDefaults: BrandDefaults;
  @Input() marketingOptInYNFromOrderValue: string;
  @Output() formChanged = new EventEmitter<any>();
  @Output() formIsValid = new EventEmitter<boolean>();
  @Output() formIsDirty = new EventEmitter<boolean>();
  @Output() submitByEnter = new EventEmitter<void>();
  @Output() selectedCountry = new EventEmitter<string>();
  @Output() customerChoosen = new EventEmitter<Customer>();
  @Output() newCustomerEvent = new EventEmitter<boolean>();

  public stateList: State[];
  @Input() set filteredStateList(value) {
    this.stateList = value;
  }
  public customerDetailsForm: UntypedFormGroup;
  public isReadOnlyCustomer = false;
  public currentCustomerSelected = false;
  private unSubscribe$ = new Subject<void>();
  public customerType = CUSTOMER_TYPE;
  public loading$ = new BehaviorSubject<boolean>(false);
  public loading = false;
  public matchedCustomer: Customer;
  public brandDefaults$: Observable<BrandDefaults>;
  public selectedCustomerCurrent: Customer;
  public isFromBackendmarketingYNValue: boolean = false ;
  constructor(
    private router: Router,
    private apiService: ApiService,
    private formBuilder: UntypedFormBuilder,
    private dialogService: DialogService,
    private store: Store<CoreUiPartialState>,
  ) {
    this.customerDetailsForm = this.formBuilder.group({
      customerNumber: [''],
      branch: [''],
      name: [''],
      firstName: ['', Validators.required],
      surname: ['', Validators.required],
      email: ['', [Validators.email]],
      phone: [''],
      phoneType: [''],
      addressLine1: [''],
      addressLine2: [''],
      suburb: [''],
      state: [''],
      postCode: [''],
      country: [''],
      secContactName: [''],
      secContactPhone: [''],
      marketingOptInYN: [''],
      useBusinessNameYN: [false],
      businessName: [''],
      zone: [''],
      carrier: [''],
      route: [''],
      freightHandlingCode: [''],
    });
  }

  ngOnInit() {
    this.customerDetailsForm.valueChanges.pipe(takeUntil(this.unSubscribe$)).subscribe((x) => {
      let formValue = this.customerDetailsForm.getRawValue();
      if (formValue.useBusinessNameYN) {
        formValue = {
          ...formValue,
          secContactName: `${formValue.firstName} ${formValue.surname}`,
          firstName: formValue.businessName,
          surname: '',
        };
      }
      formValue = {
        ...formValue,
        marketingOptInYN: formValue.marketingOptInYN,
        useBusinessNameYN: formValue.useBusinessNameYN ? 'Y' : 'N',
      };
      this.formChanged.emit(formValue);
      this.formIsValid.emit(this.customerDetailsForm.valid);
      this.formIsDirty.emit(this.customerDetailsForm.dirty);
    });

    this.customerDetailsForm.controls.marketingOptInYN.valueChanges.pipe(takeUntil(this.unSubscribe$)).subscribe((value) => {
      this.setEmailValidator(value);
      this.setPostCodeValidator(value);
    });

    this.customerDetailsForm.controls.secContactName.valueChanges.pipe(takeUntil(this.unSubscribe$)).subscribe((value) => {
      if (value !== '') {
        this.customerDetailsForm.controls.secContactPhone.setValidators([Validators.required, Validators.pattern('^[0-9 ]{10,}$')]);
      } else {
        this.customerDetailsForm.controls.secContactPhone.setValidators([Validators.pattern('^[0-9 ]{10,}$')]);
      }
      this.customerDetailsForm.controls.secContactPhone.updateValueAndValidity();
    });

    this.customerDetailsForm.controls.country.valueChanges.pipe(takeUntil(this.unSubscribe$)).subscribe((value) => {
      this.selectedCountry.emit(value);
      this.customerDetailsForm.controls.state.patchValue('');
    });

    this.customerDetailsForm.controls.useBusinessNameYN.valueChanges.pipe(takeUntil(this.unSubscribe$)).subscribe((value) => {
      if (value) {
        this.customerDetailsForm.controls.businessName.setValidators([Validators.required]);
      } else {
        this.customerDetailsForm.controls.businessName.clearValidators();
      }
      this.customerDetailsForm.controls.businessName.updateValueAndValidity();
    });

    this.customerDetailsForm.controls.phone.valueChanges
      .pipe(
        debounceTime(450),
        distinctUntilChanged(),
        filter((value) => (value || '').trim().length > 7 && !this.loading && !this.currentCustomerSelected && this.validPhone(value)),
        concatMap((value) => {
          this.loading = true;
          this.loading$.next(true);
          return this.getCustomerSpecificSearch({ searchString: value, searchType: 'P' }).pipe(takeUntil(this.unSubscribe$));
        }),
      )
      .subscribe({
        next: (resposne: any) => {
          this.matchedCustomer = resposne.CustomerMatch;
          this.loading = false;
          this.loading$.next(true);
          if (this.matchedCustomer?.customerNumber) {
            getPopup(this.dialogService, this.matchedCustomer).subscribe((val) => {
              if (val) {
                this.currentCustomerSelected = true;
                this.store.dispatch(hasUnsavedChanges({ unsavedChanges: true }));
                this.apiService
                  .fetchCustomerDetails({
                    customerNumber: this.matchedCustomer?.customerNumber.toString(),
                  })
                  .pipe(takeUntil(this.unSubscribe$))
                  .subscribe((customerDetails) => {
                    this.selectedCustomer = customerDetails.SearchResults[0];
                    callPatchValue(this.selectedCustomer, this.customerDetailsForm, this.marketingOptInYNFromOrderValue, this.brandDefaults);
                    this.setSelectedCustomer(true);
                    let formValue = this.customerDetailsForm.getRawValue();
                    this.formChanged.emit(formValue);
                    this.formIsValid.emit(this.customerDetailsForm.valid);
                    this.formIsDirty.emit(this.customerDetailsForm.dirty);
                  });
              }
            });
          }
        },
      });

    this.customerDetailsForm.controls.email.valueChanges
      .pipe(
        debounceTime(450),
        distinctUntilChanged(),
        filter((value) => (value || '').trim().length > 7 && !this.loading && !this.currentCustomerSelected && this.validEmail(value)),
        concatMap((value) => {
          this.loading = true;
          this.loading$.next(true);
          return this.getCustomerSpecificSearch({ searchString: value, searchType: 'E' }).pipe(takeUntil(this.unSubscribe$));
        }),
      )
      .subscribe({
        next: (resposne: any) => {
          this.matchedCustomer = resposne.CustomerMatch;
          this.loading = false;
          this.loading$.next(true);
          if (this.matchedCustomer?.customerNumber) {
            getPopup(this.dialogService, this.matchedCustomer).subscribe((val) => {
              if (val) {
                this.currentCustomerSelected = true;
                this.store.dispatch(hasUnsavedChanges({ unsavedChanges: true }));
                this.apiService
                  .fetchCustomerDetails({
                    customerNumber: this.matchedCustomer?.customerNumber.toString(),
                  })
                  .pipe(takeUntil(this.unSubscribe$))
                  .subscribe((customerDetails) => {
                    this.selectedCustomer = customerDetails.SearchResults[0];
                    callPatchValue(this.selectedCustomer, this.customerDetailsForm, this.marketingOptInYNFromOrderValue, this.brandDefaults);
                    this.setSelectedCustomer(true);
                    let formValue = this.customerDetailsForm.getRawValue();
                    this.formChanged.emit(formValue);
                    this.formIsValid.emit(this.customerDetailsForm.valid);
                    this.formIsDirty.emit(this.customerDetailsForm.dirty);
                  });
              }
            });
          }
        },
      });

    this.brandDefaults$ = this.store.select(getBrandDefaults);
    if (this.marketingOptInYNFromOrderValue) {
      this.customerDetailsForm.patchValue({
        marketingOptInYN: this.marketingOptInYNFromOrderValue,
      });
    }
  }

  selectCustomer(customer) {
    this.selectedCustomerCurrent = customer;
  }

  chooseExistingCustomer() {
    this.customerChoosen.emit(this.selectedCustomerCurrent);
  }

  ngOnDestroy() {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedCustomer && changes.selectedCustomer.currentValue && this.customerDetailsForm) {
      this.currentCustomerSelected = true;
      callPatchValue(this.selectedCustomer, this.customerDetailsForm, this.marketingOptInYNFromOrderValue, this.brandDefaults);
      this.customerDetailsForm.markAsPristine();

      this.setEmailValidator(this.customerDetailsForm.controls.marketingOptInYN.value);

      this.setPostCodeValidator(this.customerDetailsForm.controls.marketingOptInYN.value);

      this.isReadOnlyCustomer = this.selectedCustomer?.readOnlyCustomerYN
        ? this.selectedCustomer?.readOnlyCustomerYN === 'Y'
        : this.selectedCustomer.customerType === CUSTOMER_TYPE.billTo;
      if (this.isReadOnlyCustomer) {
        this.customerDetailsForm.disable({ emitEvent: false, onlySelf: true });
      }
      if (this.marketingOptInYNFromOrderValue) {
        this.setEmailValidator(this.marketingOptInYNFromOrderValue);
        this.setPostCodeValidator(this.marketingOptInYNFromOrderValue);
      }
    }

    if (changes.brandDefaults && changes.brandDefaults.currentValue && this.customerDetailsForm) {
      if (!this.customerDetailsForm.controls.country.value) {
        this.customerDetailsForm.patchValue({
          country: this.brandDefaults.countryCode,
        });
        this.selectedCountry.emit(this.brandDefaults.countryCode);
      }
      const phoneValidator = Validators.pattern(new RegExp(`^[0-9 ]{${this.brandDefaults.phoneDigits},}$`));
      this.customerDetailsForm.controls.phone.setValidators([phoneValidator]);
      this.customerDetailsForm.controls.secContactPhone.setValidators([phoneValidator]);
    }
  }
  validEmail(value) {
    return value
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      );
  }

  validPhone(value) {
    return value.match(`^[0-9 ]{${this.brandDefaults.phoneDigits},}$`);
  }

  setEmailValidator(value) {
    if (value === 'Y' || value === 'R') {
      this.customerDetailsForm.controls.email.setValidators([Validators.required, Validators.email]);
    } else {
      this.customerDetailsForm.controls.email.setValidators([]);
    }
    this.customerDetailsForm.controls.email.updateValueAndValidity();
  }

  setPostCodeValidator(value) {
    if (value === 'Y' || value === 'R') {
      this.customerDetailsForm.controls.postCode.setValidators([Validators.required]);
    } else {
      this.customerDetailsForm.controls.postCode.setValidators([]);
    }
    this.customerDetailsForm.controls.postCode.updateValueAndValidity();
  }

  pressEnter() {
    this.submitByEnter.emit();
  }

  getCustomerSpecificSearch(parameters: any) {
    return this.apiService.customerSpecificSearch({
      searchString: parameters.searchString,
      searchType: parameters.searchType,
    });
  }

  setSelectedCustomer(value: boolean) {
    this.newCustomerEvent.emit(value);
  }
}
function getPopup(dialogService: DialogService, matchedCustomer: Customer) {
  return dialogService.confirm(
    ` <div>
        ${matchedCustomer.customerName ? '<label>Customer:</label> ' + matchedCustomer.customerName : ''}
      </div>
      <div>
        ${matchedCustomer.customerType ? '<label>Customer type:</label> ' + matchedCustomer.customerType : ''}
      </div>
      <div>
        ${matchedCustomer.email ? '<label>Email:</label> ' + matchedCustomer.email : ''}
      </div>
      <div>
        ${matchedCustomer.phone ? '<label>Phone:</label> ' + matchedCustomer.phone : ''}
      </div>
      <div>
        ${matchedCustomer.postCode ? '<label>Post Code:</label> ' + matchedCustomer.postCode : ''}
      </div>
      `,
    `Match Found!  ${matchedCustomer.customerNumber ? '  Customer Number:' + matchedCustomer.customerNumber : ''} `,
    'USE EXISTING CUSTOMER',
    'CONTINUE ADDING NEW CUSTOMER',
    true,
    false,
    true,
  );
}

function callPatchValue(
  selectedCustomer: CustomerDetails,
  customerDetailsForm: UntypedFormGroup,
  marketingOptInYNFromOrderValue: string,
  brandDefaults: BrandDefaults,
) {
  if(selectedCustomer.marketingYN === 'Y'){
    this.isFromBackendmarketingYNValue = true;
  }
  customerDetailsForm.patchValue(
    {
      customerNumber: selectedCustomer.customerNumber,
      branch: selectedCustomer.branchCode,
      name: selectedCustomer.customerName,
      firstName: selectedCustomer.firstName,
      surname: selectedCustomer.surname,
      email: selectedCustomer.email,
      phone: selectedCustomer.phone,
      phoneType: selectedCustomer.phoneType,
      addressLine1: selectedCustomer.addressLine1,
      addressLine2: selectedCustomer.addressLine2,
      suburb: selectedCustomer.suburb,
      state: selectedCustomer.stateCode,
      postCode: selectedCustomer.postCode,
      country: selectedCustomer.countryCode === '' ? brandDefaults.countryCode : selectedCustomer.countryCode,
      secContactName: selectedCustomer.secondaryContact,
      secContactPhone: selectedCustomer.secondaryContactPhone,
      marketingOptInYN: marketingOptInYNFromOrderValue ? marketingOptInYNFromOrderValue : (selectedCustomer.marketingYN ?? ''),
      useBusinessNameYN: selectedCustomer.useBusinessNameYN === 'Y',
      businessName: selectedCustomer.useBusinessNameYN === 'Y' ? selectedCustomer.customerName : '',
      zone: selectedCustomer.zoneNumber,
      carrier: selectedCustomer.carrierNumber,
      route: selectedCustomer.routeCode,
      freightHandlingCode: selectedCustomer.freightCode,
    },
    {
      emitEvent: false,
      onlySelf: true,
    },
  );
}

